// Drop-in WYSIWYG editor for emails
// Replaces the live preview with editable content. Click any text to edit it.
// Floating toolbar appears when text is selected: bold, italic, color, link.

import { useEffect, useRef, useState, useCallback } from "react";

const C = {
  surface:"#ffffff", border:"#e8e4df", gold:"#b48c3c", goldD:"#8a6f2e",
  goldBg:"rgba(180,140,60,0.08)", borderGold:"rgba(180,140,60,0.3)",
  text:"#1a1a2e", muted:"#6b7280", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";

// Use unicode bracket markers that cannot appear in normal user text.
// This way we can safely round-trip {{first_name}} <-> visible "Sarah"
const FIRST_NAME_MARKER = "\u2987FIRST_NAME\u2988";
const NAME_MARKER = "\u2987NAME\u2988";

function renderPreview(html) {
  if (!html) return "";
  // Step 1: replace {{first_name}} with marker
  let out = html.replaceAll("{{first_name}}", FIRST_NAME_MARKER).replaceAll("{{name}}", NAME_MARKER);
  // Step 2: render markers as visible "Sarah" inside a span (so we can find them later)
  out = out.replaceAll(FIRST_NAME_MARKER, '<span data-personalize="first_name" style="background:rgba(180,140,60,0.15);color:#b48c3c;padding:0 4px;border-radius:3px">Sarah</span>');
  out = out.replaceAll(NAME_MARKER, '<span data-personalize="name" style="background:rgba(180,140,60,0.15);color:#b48c3c;padding:0 4px;border-radius:3px">Sarah Smith</span>');
  return out;
}

// When saving, convert the personalize spans back to {{first_name}}/{{name}}
function unrenderPreview(html) {
  if (!html) return "";
  // Replace any span with data-personalize attribute back to template token
  let out = html.replace(/<span[^>]*data-personalize="first_name"[^>]*>[^<]*<\/span>/gi, "{{first_name}}");
  out = out.replace(/<span[^>]*data-personalize="name"[^>]*>[^<]*<\/span>/gi, "{{name}}");
  return out;
}

// Reverse - turn "Sarah" back to {{first_name}} when saving back to template
// We use a HTML comment marker approach to be safe — actually we just save what's edited as-is
// since users typically edit their own custom content, not the {{first_name}} parts.

export default function EmailWysiwyg({ html, onChange, mode = "desktop" }) {
  const editorRef = useRef(null);
  const [toolbarPos, setToolbarPos] = useState(null);
  const [linkInput, setLinkInput] = useState({ open: false, url: "" });
  const isUpdatingFromProps = useRef(false);

  // Set initial content (only when html prop changes externally, not on user edit)
  useEffect(() => {
    if (!editorRef.current) return;
    const rendered = renderPreview(html);
    if (editorRef.current.innerHTML !== rendered) {
      isUpdatingFromProps.current = true;
      editorRef.current.innerHTML = rendered;
      setTimeout(() => { isUpdatingFromProps.current = false; }, 0);
    }
  }, [html]);

  // When user types/edits, save back
  const handleInput = useCallback(() => {
    if (isUpdatingFromProps.current) return;
    if (!editorRef.current) return;
    const newHtml = editorRef.current.innerHTML;
    // Use marker-based unrender to safely convert spans back to {{first_name}}/{{name}}
    onChange(unrenderPreview(newHtml));
  }, [onChange]);

  // Show floating toolbar when text is selected
  const handleSelectionChange = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !editorRef.current) {
      setToolbarPos(null);
      return;
    }
    if (!editorRef.current.contains(sel.anchorNode)) {
      setToolbarPos(null);
      return;
    }
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      setToolbarPos(null);
      return;
    }
    setToolbarPos({
      top: rect.top + window.scrollY - 50,
      left: rect.left + window.scrollX + rect.width / 2,
    });
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
  }, [handleSelectionChange]);

  // Toolbar actions
  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    handleInput();
    editorRef.current?.focus();
  };

  const applyColor = (color) => exec("foreColor", color);
  const applyLink = () => {
    setLinkInput({ open: true, url: "" });
  };
  const saveLink = () => {
    if (!linkInput.url) return;
    exec("createLink", linkInput.url);
    // Make links open in new tab
    setTimeout(() => {
      editorRef.current?.querySelectorAll("a").forEach(a => {
        a.target = "_blank";
        a.rel = "noopener noreferrer";
      });
      handleInput();
    }, 50);
    setLinkInput({ open: false, url: "" });
  };
  const removeLink = () => exec("unlink");

  const colors = ["#1a1a2e", C.gold, C.goldD, "#c0392b", "#2e8b57", "#2563eb", "#7c3aed", "#6b7280"];

  return (
    <>
      <div style={{
        maxWidth: mode === "mobile" ? 380 : 600,
        margin: "0 auto",
        background: "#fff",
        borderRadius: 6,
        overflow: "hidden",
        boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
      }}>
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onBlur={handleInput}
          spellCheck={false}
          style={{
            outline: "none",
            minHeight: 200,
            cursor: "text",
          }}
        />
      </div>

      {/* Floating toolbar */}
      {toolbarPos && !linkInput.open && (
        <div style={{
          position: "fixed",
          top: toolbarPos.top,
          left: toolbarPos.left,
          transform: "translateX(-50%)",
          background: "#0f0f1e",
          color: "#fff",
          borderRadius: 8,
          padding: "6px 8px",
          display: "flex",
          alignItems: "center",
          gap: 4,
          boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
          zIndex: 3000,
          fontFamily: F,
          fontSize: 12,
        }} onMouseDown={(e) => e.preventDefault()}>
          <ToolBtn onClick={() => exec("bold")} title="Bold (Ctrl+B)"><b>B</b></ToolBtn>
          <ToolBtn onClick={() => exec("italic")} title="Italic"><i>I</i></ToolBtn>
          <ToolBtn onClick={() => exec("underline")} title="Underline"><u>U</u></ToolBtn>
          <Sep />
          <ToolBtn onClick={() => exec("formatBlock", "h1")} title="Heading 1">H1</ToolBtn>
          <ToolBtn onClick={() => exec("formatBlock", "h2")} title="Heading 2">H2</ToolBtn>
          <ToolBtn onClick={() => exec("formatBlock", "p")} title="Paragraph">P</ToolBtn>
          <Sep />
          {colors.map(col => (
            <button
              key={col}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyColor(col)}
              title={`Color ${col}`}
              style={{
                width: 18, height: 18,
                background: col,
                border: "1.5px solid rgba(255,255,255,0.3)",
                borderRadius: "50%",
                cursor: "pointer",
                padding: 0,
              }}
            />
          ))}
          <Sep />
          <ToolBtn onClick={applyLink} title="Add link">🔗</ToolBtn>
          <ToolBtn onClick={removeLink} title="Remove link">✕🔗</ToolBtn>
          <Sep />
          <ToolBtn onClick={() => exec("justifyLeft")} title="Align left">⬅</ToolBtn>
          <ToolBtn onClick={() => exec("justifyCenter")} title="Center">↔</ToolBtn>
        </div>
      )}

      {/* Link input dialog */}
      {linkInput.open && (
        <div style={{
          position: "fixed",
          top: toolbarPos?.top || 100,
          left: toolbarPos?.left || 200,
          transform: "translateX(-50%)",
          background: "#0f0f1e",
          color: "#fff",
          borderRadius: 8,
          padding: 10,
          display: "flex",
          gap: 6,
          boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
          zIndex: 3000,
          fontFamily: F,
        }} onMouseDown={(e) => e.preventDefault()}>
          <input
            autoFocus
            type="url"
            value={linkInput.url}
            onChange={(e) => setLinkInput({ ...linkInput, url: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") saveLink(); if (e.key === "Escape") setLinkInput({ open: false, url: "" }); }}
            placeholder="https://..."
            style={{
              padding: "6px 10px",
              background: "#1a1a2e",
              border: "1px solid #2a2a45",
              borderRadius: 5,
              color: "#fff",
              fontSize: 12,
              width: 240,
              outline: "none",
            }}
          />
          <button onClick={saveLink} style={{ padding:"6px 12px",background:C.gold,color:"#fff",border:"none",borderRadius:5,cursor:"pointer",fontSize:11,fontWeight:700 }}>Add</button>
          <button onClick={() => setLinkInput({ open: false, url: "" })} style={{ padding:"6px 10px",background:"transparent",color:"#fff",border:"1px solid #2a2a45",borderRadius:5,cursor:"pointer",fontSize:11 }}>Cancel</button>
        </div>
      )}
    </>
  );
}

function ToolBtn({ children, onClick, title }) {
  return (
    <button
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      style={{
        background: "transparent",
        border: "none",
        color: "#fff",
        cursor: "pointer",
        padding: "4px 7px",
        borderRadius: 4,
        fontSize: 13,
        fontWeight: 600,
        fontFamily: F,
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
    >
      {children}
    </button>
  );
}
function Sep() {
  return <div style={{ width: 1, height: 18, background: "rgba(255,255,255,0.2)" }} />;
}
